import { useState, type FormEvent } from "react";

import type {
  Counterparty,
  CounterpartyContactInput,
} from "../entities/counterparties";
import {
  validateEmail,
  validateInn,
  validateKpp,
} from "../entities/counterparties";
import {
  ContactFields,
  DomainListField,
  TextField,
} from "./CounterpartyFields";
import {
  contactInput,
  emptyContactDraft,
  type ContactDraft,
} from "./contactDraft";
import { requestErrorText, serverFieldErrors } from "./counterpartiesSupport";
import { Notice } from "./Notice";

export interface CounterpartyFormValues {
  name: string;
  /** `null` очищает реквизит: пустая строка сервер не примет. */
  inn: string | null;
  kpp: string | null;
  notes: string | null;
  domains: string[];
  contacts: CounterpartyContactInput[];
}

type Touched = Partial<Record<"name" | "inn" | "kpp", boolean>>;

function localErrors(
  name: string,
  inn: string,
  kpp: string,
  notes: string,
): Record<"name" | "inn" | "kpp" | "notes", string | null> {
  return {
    name:
      name.trim() === ""
        ? "Укажите название контрагента"
        : name.trim().length > 255
          ? "Название длиннее 255 символов"
          : null,
    inn: validateInn(inn),
    kpp: validateKpp(kpp),
    notes: notes.length > 10000 ? "Примечание длиннее 10 000 символов" : null,
  };
}

/**
 * Форма реквизитов. При создании в ней же собираются контакты; при
 * редактировании контакты остаются в карточке, потому что Core меняет их
 * отдельными операциями: добавить и удалить.
 */
export function CounterpartyForm({
  counterparty,
  pending,
  error,
  onSubmit,
  onCancel,
  onChange,
}: {
  /** Есть — редактирование, нет — создание. */
  counterparty?: Counterparty;
  pending: boolean;
  error: unknown;
  onSubmit: (values: CounterpartyFormValues) => void;
  onCancel: () => void;
  /** Человек изменил данные: прежний ключ идемпотентности больше не годится. */
  onChange?: () => void;
}) {
  const editing = counterparty !== undefined;
  const [name, setName] = useState(counterparty?.name ?? "");
  const [inn, setInn] = useState(counterparty?.inn ?? "");
  const [kpp, setKpp] = useState(counterparty?.kpp ?? "");
  const [notes, setNotes] = useState(counterparty?.notes ?? "");
  const [domains, setDomains] = useState<string[]>(counterparty?.domains ?? []);
  const [contacts, setContacts] = useState<CounterpartyContactInput[]>([]);
  const [contactDraft, setContactDraft] =
    useState<ContactDraft>(emptyContactDraft);
  const [contactError, setContactError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Touched>({});
  const [submitted, setSubmitted] = useState(false);

  const errors = localErrors(name, inn, kpp, notes);
  const server = serverFieldErrors(error);
  const shown = (field: "name" | "inn" | "kpp"): string | null =>
    submitted || touched[field] === true
      ? (errors[field] ?? server[field] ?? null)
      : (server[field] ?? null);

  function edit<T>(setter: (value: T) => void) {
    return (value: T) => {
      onChange?.();
      setter(value);
    };
  }

  function addContactToList() {
    const problem = validateEmail(contactDraft.email);
    if (problem !== null) {
      setContactError(problem);
      return;
    }
    const email = contactDraft.email.trim();
    if (contacts.some((c) => c.email.toLowerCase() === email.toLowerCase())) {
      setContactError("Этот email уже добавлен в список");
      return;
    }
    onChange?.();
    setContacts([...contacts, contactInput(contactDraft)]);
    setContactDraft(emptyContactDraft);
    setContactError(null);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    if (Object.values(errors).some((value) => value !== null)) return;
    // Недобавленный контакт не должен потеряться молча.
    if (!editing && contactDraft.email.trim() !== "") {
      setContactError(
        "Нажмите «Добавить в список» или очистите поле, иначе этот контакт не сохранится",
      );
      return;
    }
    onSubmit({
      name: name.trim(),
      inn: inn.trim() === "" ? null : inn.trim(),
      kpp: kpp.trim() === "" ? null : kpp.trim(),
      notes: notes.trim() === "" ? null : notes.trim(),
      domains,
      contacts,
    });
  }

  return (
    <form className="cp-form" onSubmit={submit} noValidate>
      <h2 className="cp-form__title">
        {editing ? "Изменение реквизитов" : "Новый контрагент"}
      </h2>
      {error === null || error === undefined ? null : (
        <Notice>
          {requestErrorText(error, "Не удалось сохранить контрагента.")}
        </Notice>
      )}
      <TextField
        label="Название"
        required
        maxLength={255}
        value={name}
        error={shown("name")}
        onBlur={() => setTouched((state) => ({ ...state, name: true }))}
        onChange={edit(setName)}
      />
      <div className="cp-form__row">
        <TextField
          label="ИНН"
          inputMode="numeric"
          maxLength={12}
          autoComplete="off"
          hint="10 цифр для организации, 12 для ИП"
          value={inn}
          error={shown("inn")}
          onBlur={() => setTouched((state) => ({ ...state, inn: true }))}
          onChange={edit(setInn)}
        />
        <TextField
          label="КПП"
          inputMode="numeric"
          maxLength={9}
          autoComplete="off"
          hint="9 цифр"
          value={kpp}
          error={shown("kpp")}
          onBlur={() => setTouched((state) => ({ ...state, kpp: true }))}
          onChange={edit(setKpp)}
        />
      </div>
      <TextField
        label="Примечание"
        multiline
        maxLength={10000}
        value={notes}
        error={errors.notes ?? server.notes ?? null}
        onChange={edit(setNotes)}
      />
      <DomainListField
        domains={domains}
        error={server.domains ?? null}
        onChange={edit(setDomains)}
      />

      {editing ? null : (
        <fieldset className="cp-fieldset">
          <legend>Контакты</legend>
          {contacts.length === 0 ? null : (
            <ul className="cp-contact-list" aria-label="Контакты в списке">
              {contacts.map((contact) => (
                <li key={contact.email}>
                  <span>
                    <strong>{contact.email}</strong>
                    {contact.name ? ` · ${contact.name}` : ""}
                    {contact.is_primary ? " · основной" : ""}
                  </span>
                  <button
                    type="button"
                    className="button button--text button--compact"
                    aria-label={`Убрать контакт ${contact.email}`}
                    onClick={() =>
                      setContacts(contacts.filter((c) => c !== contact))
                    }
                  >
                    Убрать
                  </button>
                </li>
              ))}
            </ul>
          )}
          <ContactFields
            draft={contactDraft}
            showErrors={contactError !== null}
            serverErrors={contactError === null ? {} : { email: contactError }}
            onChange={(draft) => {
              setContactDraft(draft);
              setContactError(null);
            }}
          />
          <button
            type="button"
            className="button button--secondary button--compact"
            onClick={addContactToList}
          >
            Добавить в список
          </button>
        </fieldset>
      )}

      <div className="dw-form-actions">
        <button className="button button--primary" disabled={pending}>
          {pending
            ? "Сохраняем…"
            : editing
              ? "Сохранить"
              : "Создать контрагента"}
        </button>
        <button
          className="button button--text"
          type="button"
          onClick={onCancel}
          disabled={pending}
        >
          Отмена
        </button>
      </div>
    </form>
  );
}
